import { decodeExchangeName } from "@/utils/exchangeUtils";
import Coin from "./Coin";
import OpenOrder from "./OpenOrder";
import StopLossModel from "./stop-loss/StopLossModel";

/**
 * 📈
 * Model representing the stats of a certain trading bot
 */
export default class TradingBotStats {
  public portfolioValue: number;
  public exchange: string;
  public open_orders?: any;
  public avg_fee: number;
  public avg_roi_trade: number;
  public avg_roi_month: number;
  public stop_loss?: StopLossModel;
  public volume: number;

  constructor(
    public curBalance: number,
    public tokBalance: number,
    portfolioValue: number,
    public uid: string,
    exchange: string,
    public strategy_name: string,
    public in_fees: number,
    public market: string,
    public candles: string,
    public trades_count: number,
    public trade_volume: number,
    public startingBalance: number[],
    public start_time: number,
    public stop_time: number,
    public roi: number,
    public bot_profit: number,
    public bah_roi: number,
    public bah_profit: number,
    public enabled: boolean,
    avg_fee: number,
    avg_roi_trade: number,
    avg_roi_month: number,
    public image: string,
    public sharpe: number,
    public sortino: number,
    public maximum_drawdown: number,
    public last_trade_attempt?: number,
    open_orders?: any,
    stop_loss?: any
  ) {
    this.portfolioValue = portfolioValue ? portfolioValue : curBalance;
    this.exchange = decodeExchangeName(exchange);
    this.open_orders = this.mapOpenOrders(open_orders);
    this.avg_fee = avg_fee * 100;
    this.avg_roi_trade = avg_roi_trade * 100;
    this.avg_roi_month = avg_roi_month * 100;
    this.stop_loss = stop_loss
      ? new StopLossModel(
          stop_loss.stop,
          stop_loss.trailing,
          stop_loss.starting_portfolio,
          stop_loss.highest_portfolio
        )
      : undefined;
    this.volume = this.trade_volume
  }

  /**
   * Converts a raw open orders object into an array of open orders
   * @param rawOpenOrders
   */
  private mapOpenOrders(rawOpenOrders: any): OpenOrder[] {
    if (rawOpenOrders == undefined) {
      return [];
    }

    let openOrders: any[] = Object.values(rawOpenOrders);

    if (openOrders.length == 0) {
      return [];
    }

    return openOrders.map((rawOpenOrder) => {
      return new OpenOrder(
        this.uid,
        this.strategy_name,
        rawOpenOrder.txid,
        rawOpenOrder.side.toUpperCase(),
        rawOpenOrder.volume,
        rawOpenOrder.price,
        rawOpenOrder.expire_time,
        rawOpenOrder.createtime,
        rawOpenOrder.org_vol,
        this.market,
        rawOpenOrder.order_type,
        this.market.split(":")[0],
        this.market.split(":")[1]
      );
    });
  }

  static newInstance(response: any): TradingBotStats {
    return new TradingBotStats(
      response.curBalance,
      response.tokBalance,
      response.portfolioValue,
      response.uid,
      response.exchange,
      response.strategy_name,
      response.in_fees,
      response.market,
      response.candles,
      response.trades_count,
      response.trade_volume,
      response.startingBalance,
      response.start_time,
      response.stop_time,
      response.roi,
      response.bot_profit,
      response.bah_roi,
      response.bah_profit,
      response.enabled,
      response.avg_fee,
      response.avg_roi_trade,
      response.avg_roi_month,
      response.image,
      response.sharpe,
      response.sortino,
      response.maximum_drawdown,
      response.last_trade_attempt,
      response.open_orders,
      response.stop_loss
    );
  }

  get currencyUsedForTrading(): string {
    return this.market.toString().split(":")[1];
  }

  get currencyBought(): string {
    return this.market.toString().split(":")[0];
  }

  get balanceInUsePerCoin(): Coin[] {
    const coinsUsed: Coin[] = [];

    let baseCurrencyInOrder = 0;
    let primaryCurrencyInOrder = 0;

    // Take into consideration the amount in open orders
    this.open_orders.forEach((order: OpenOrder) => {
      switch (order.side) {
        case "BUY":
          baseCurrencyInOrder += order.volume * order.price;
          break;
        case "SELL":
          primaryCurrencyInOrder += order.volume;
          break;
      }
    });

    const primaryCurrencyValue =
      this.portfolioValue - this.curBalance - baseCurrencyInOrder;
    const primaryCurrencyPercentageOfAll =
      (primaryCurrencyValue / this.portfolioValue) * 100;

    const baseCurrencyValue = this.curBalance + baseCurrencyInOrder;
    const baseCurrencyPercentageOfAll =
      (baseCurrencyValue / this.portfolioValue) * 100;

    coinsUsed.push(
      new Coin(
        this.market.split(":")[0],
        primaryCurrencyValue,
        primaryCurrencyPercentageOfAll < 0
          ? 0
          : primaryCurrencyPercentageOfAll > 100
          ? 100
          : primaryCurrencyPercentageOfAll,
        this.tokBalance + primaryCurrencyInOrder
      )
    );
    coinsUsed.push(
      new Coin(
        this.market.split(":")[1],
        baseCurrencyValue,
        baseCurrencyPercentageOfAll < 0
          ? 0
          : baseCurrencyPercentageOfAll > 100
          ? 100
          : baseCurrencyPercentageOfAll,
        baseCurrencyValue
      )
    );
    return coinsUsed.sort(
      (coinA: Coin, coinB: Coin) => coinB.value - coinA.value
    );
  }

  /**
   * USED FOR TESTING ONLY
   * Example of a running bot
   */
  static example: TradingBotStats = new TradingBotStats(
    110,
    0,
    50,
    "1",
    "Kraken",
    "MA Crossover",
    0,
    "BTC:USDT",
    "15m",
    54,
    10000,
    [0, 100],
    new Date().getTime(),
    new Date().getTime(),
    -0.004814253,
    0.15,
    0.15,
    0.15,
    true,
    0.012,
    0.15,
    0.13,
    "url",
    0.002,
    0.002,
    0.01,
    new Date().getTime(),
    TradingBotStats.generateOpenOrders()
  );

  /**
   * USED FOR TESTING ONLY
   * Generates 5 to 10 random open orders
   */
  private static generateOpenOrders() {
    let exmapleOpenOrders: any = {};

    for (let i = 0; i < Math.random() * 5 + 5; i++) {
      let id = (Math.random() * 15 + 1).toString();
      exmapleOpenOrders[id] = {
        txId: id,
        side: i % 2 == 0 ? "buy" : "sell",
        vol: Math.random() * 50 + 50,
        price: Math.random() * 50 + 50,
        expire_time:
          Math.floor(Math.random() * (1600353061 - 1600243000)) + 1600243000,
        createtime:
          Math.floor(Math.random() * (1600353061 - 1600243000)) + 1600243000,
        org_volume: Math.random() * 50 + 50,
      };
    }

    return exmapleOpenOrders;
  }
}
